import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { map } from "rxjs/operators";
import {
  BuildingRentRoll,
  Corporation,
  Tenant,
  TenantSearchResponse
} from "../models/tenant.model";
import * as moment from "moment";

@Injectable({
  providedIn: "root"
})
export class MasterTenantsService {
  constructor(private http: HttpClient) {}

  getMasterTenants(
    page,
    per_page,
    filterName?,
    filterIndustry?,
    filterCountry?,
    filterDate?,
    filterStatus?,
    filterContact?,
    filterCorporationPropertyType?,
    filterDateOptions?,
    filterActiveStatus?,
    filterUpdateDate?,
    filterModifiedDateOptions?,
    createdDateFilters?,
    updatedDateFilters?
  ) {
    const filters = {
      page: page,
      perPage: per_page,
      filterName: filterName,
      filterIndustry: filterIndustry,
      filterCountry: filterCountry,
      year: filterDateOptions?.year ?? null,
      includeThisQuarter: filterDateOptions?.thisQuarter ?? false,
      includeLastQuarter: filterDateOptions?.lastQuarter ?? false,
      includedInStats: filterStatus,
      filterContact: filterContact,
      filterCorporationPropertyType: filterCorporationPropertyType,
      status: filterActiveStatus,
      filterUpdateDate: filterUpdateDate
        ? moment(filterUpdateDate).format("YYYY-MM-DD")
        : null,
      yearModifiedDate: filterModifiedDateOptions?.yearModifiedDate ?? null,
      includeThisQuarterModifiedDate:
        filterModifiedDateOptions?.thisQuarterModifiedDate ?? false,
      includeLastQuarterModifiedDate:
        filterModifiedDateOptions?.lastQuarterModifiedDate ?? false,
      createdDateFilters: createdDateFilters ?? [],
      updatedDateFilters: updatedDateFilters ?? []
    };

    return this.http
      .post<TenantSearchResponse>(
        `${environment.apiURL}/corporations/tenant/list`,
        filters
      )
      .toPromise();
  }

  getTenantDateFilters() {
    return this.http
      .get(`${environment.apiURL}/corporations/tenant/date-filters`)
      .toPromise();
  }

  getIndustries() {
    return this.http
      .get(`${environment.apiURL}/building-rent-rolls/segments`)
      .toPromise();
  }

  updateTenant(tenant: Tenant) {
    return this.http
      .put(`${environment.apiURL}/corporations/tenant/`, tenant)
      .toPromise();
  }

  createTenant(tenant: Tenant) {
    return this.http
      .post(`${environment.apiURL}/corporations/tenant/`, tenant)
      .toPromise();
  }

  getCountries() {
    return this.http
      .get(`${environment.apiURL}/corporations/tenant/country`)
      .toPromise();
  }

  getCorporationDetail(corporationId) {
    return this.http
      .get<Corporation>(
        `${environment.apiURL}/corporation/${corporationId}/info`
      )
      .toPromise();
  }

  deactivateCorporation(corporationId) {
    return this.http
      .post(`${environment.apiURL}/corporation/${corporationId}/deactivate`, {})
      .toPromise();
  }

  activateCorporation(corporationId) {
    return this.http
      .post(`${environment.apiURL}/corporation/${corporationId}/activate`, {})
      .toPromise();
  }

  getTenantsDetail(id) {
    return this.http.get(`${environment.apiURL}/buildings/${id}`).toPromise();
  }

  getLastDayQuarter(buildingTypeId) {
    return this.http
      .get(
        `${environment.apiURL}/building-tenant/research/last-day-quarter/${buildingTypeId}`
      )
      .toPromise();
  }

  getBuilding(id) {
    return this.http
      .get<BuildingRentRoll[]>(
        `${environment.apiURL}/building-rent-rolls?building=${id}`
      )
      .pipe(
        map((resp: BuildingRentRoll[]) => {
          return resp.map(item => {
            const [year, month, day] = item.dateEntered as [
              number,
              number,
              number
            ];
            item.dateEntered = new Date(
              moment.utc({ year, month: month - 1, day }).format("MM/DD/YYYY")
            );
            if (item.publishedDate) {
              const [year2, month2, day2] = item.publishedDate as [
                number,
                number,
                number
              ];
              item.publishedDate = new Date(
                moment
                  .utc({ year: year2, month: month2 - 1, day: day2 })
                  .format("MM/DD/YYYY")
              );
            }
            return item;
          });
        })
      )
      .toPromise();
  }

  updateTenantDetail(buildingId: number, listTenants) {
    return this.http
      .put(
        `${environment.apiURL}/building-rent-rolls?building=${buildingId}`,
        listTenants
      )
      .toPromise();
  }

  downloadTenantDetail(id) {
    let download = {
      buildingIds: [id],
      excelOptions: 2
    };

    return this.http
      .post(
        `${environment.apiURL}/buildingPrints/buildingExcelReport`,
        download
      )
      .toPromise();
  }

  getCorporations() {
    return this.http
      .get(`${environment.apiURL}/corporations/any-status`)
      .toPromise();
  }

  getIndustryReatil() {
    return this.http
      .get(`${environment.apiURL}/lookUpTables/industryRetail`)
      .toPromise();
  }

  getSpaceReatil() {
    return this.http.get(`${environment.apiURL}/space-type-retail`).toPromise();
  }
}
